import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  Accordion,
  FilterAccordionHeader,
  TextField,
} from '@folio/stripes/components';
import {
  stripesConnect,
  withStripes,
} from '@folio/stripes/core';

import { getFullName } from '@folio/stripes/util';
import { formatCurrentUserFromStripes } from '../../../../utils';
import UserSelection from '../../user-selection';

const clearFilterHandler = (onChange, name) => () => {
  onChange({ name, values: [] });
};

const UserFilter = ({
  id,
  activeFilters,
  closedByDefault,
  labelId,
  mutator,
  name,
  onChange,
  stripes,
}) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const intl = useIntl();

  const selectUser = useCallback(
    (user) => {
      setSelectedUser(user);
      onChange({ name, values: [user.id] });
    },
    [name, onChange],
  );

  useEffect(
    () => {
      let isSubscribed = true;

      if (activeFilters && activeFilters[0] && !selectedUser) {
        mutator.filterUser.GET({ path: `users/${activeFilters[0]}` })
          .then((value) => {
            if (isSubscribed) {
              setSelectedUser(value);
            }
          });
      }

      return () => {
        isSubscribed = false;
      };
    },
    [activeFilters, selectUser],
  );

  const clearFilter = useMemo(() => clearFilterHandler(onChange, name), [onChange, name]);
  const selectedUserName = (
    activeFilters && activeFilters[0] && selectedUser && getFullName(selectedUser)
  ) || '';

  return (
    <Accordion
      data-test-user-filter-accordion
      closedByDefault={closedByDefault}
      displayClearButton={activeFilters?.length > 0}
      header={FilterAccordionHeader}
      id={id}
      label={<FormattedMessage id={labelId} />}
      onClearFilter={clearFilter}
    >
      <TextField
        data-test-user-filter
        disabled
        marginBottom0
        aria-label={intl.formatMessage({ id: 'ui-task-list.user.assignedUserFilter' })}
        type="text"
        value={selectedUserName}
      />
      <UserSelection
        hasAssignCurrent
        assignCurrentLabelId="ui-task-list.user.assignMe"
        onCurrentUserSelect={() => selectUser(formatCurrentUserFromStripes(stripes.user.user))}
        onUserSelect={selectUser}
      />
    </Accordion>
  );
};

UserFilter.manifest = {
  filterUser: {
    throwErrors: false,
    type: 'okapi',
    perRequest: 100,
    path: 'users',
    accumulate: true,
    fetch: false,
  },
};

UserFilter.propTypes = {
  id: PropTypes.string.isRequired,
  labelId: PropTypes.string.isRequired,
  mutator: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  stripes: PropTypes.shape({
    user: PropTypes.shape({
      user: PropTypes.object.isRequired,
    }).isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  activeFilters: PropTypes.arrayOf(PropTypes.string),
  closedByDefault: PropTypes.bool,
};

UserFilter.defaultProps = {
  closedByDefault: false,
};

export default withStripes(stripesConnect(UserFilter));
